// Grouped table view for the Deliverable tab — mirrors the milestone
// grouped table pattern (MilestoneGroupedTable). A single continuous
// <table> renders one <tbody> per non-empty group; each tbody opens
// with a heading <tr><td colspan> button that toggles expansion. Every
// group is collapsible; Completed defaults collapsed, the rest default
// expanded. Per-group state persists in localStorage under
// `deliverables.group.<key>.collapsed`.
//
// Group placement is computed upstream by groupRowsByStatus, keeping
// this component a thin renderer.

import React, { useCallback, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import {
  DELIVERABLE_TABLE_COLUMN_COUNT,
  DeliverableTableBodyRows,
  DeliverableTableHeadRow,
  type DeliverableAction,
  type DeliverableSortColumn,
  type DeliverableSortDirection,
} from "./DeliverableTable";
import type {
  ChildRow,
  OneOffRow,
  OrphanRow,
  Row,
} from "./groupDeliverables";
import type {
  DeliverableGroupKey,
  DeliverableRowGroup,
} from "./deliverableGrouping";
import { GROUP_ORDER } from "./deliverableGrouping";
import type { Deliverable, Milestone, RagRating } from "../../../../types";

/** Only these groups expose a collapse affordance. Per locked spec
 *  the other three buckets are always-open — needs_attention,
 *  awaiting_customer_action, and upcoming represent live work the
 *  user must see, so we never hide them. Completed is historical
 *  context and benefits from being foldable. Mirrors
 *  MilestoneGroupedTable's COLLAPSIBLE_KEYS pattern. */
const COLLAPSIBLE_KEYS: ReadonlySet<DeliverableGroupKey> = new Set([
  "completed",
]);

const DEFAULT_COLLAPSED: ReadonlySet<DeliverableGroupKey> = new Set([
  "completed",
]);

const COLLAPSE_STORAGE_PREFIX = "deliverables.group.";

function readCollapsedFromStorage(key: DeliverableGroupKey): boolean {
  if (typeof window === "undefined") return DEFAULT_COLLAPSED.has(key);
  try {
    const raw = window.localStorage.getItem(
      `${COLLAPSE_STORAGE_PREFIX}${key}.collapsed`,
    );
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch {
    // ignore
  }
  return DEFAULT_COLLAPSED.has(key);
}

function writeCollapsedToStorage(
  key: DeliverableGroupKey,
  collapsed: boolean,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${COLLAPSE_STORAGE_PREFIX}${key}.collapsed`,
      collapsed ? "1" : "0",
    );
  } catch {
    // best-effort; in-memory state still updates
  }
}

function readAllCollapsed(): Record<DeliverableGroupKey, boolean> {
  return {
    needs_attention: readCollapsedFromStorage("needs_attention"),
    awaiting_customer_action: readCollapsedFromStorage(
      "awaiting_customer_action",
    ),
    upcoming: readCollapsedFromStorage("upcoming"),
    completed: readCollapsedFromStorage("completed"),
  };
}

export interface DeliverableGroupedViewProps {
  /** Pre-bucketed row groups in GROUP_ORDER. Each entry preserves the
   *  parent/child/one-off Row shape so recurring children stay
   *  attached to their parents inside the group. */
  groups: DeliverableRowGroup[];
  /** Optional flat item list — kept on the prop surface for callers
   *  that want to drive supplementary lookups. Unused inside the
   *  component itself. */
  items?: readonly Deliverable[];
  /** Set of recurring parent ids currently expanded. Shared across all
   *  groups so the user's expand state survives group re-bucketing. */
  expandedScheduleIds: Set<string>;
  onToggleExpand: (parentId: string) => void;
  onRowClick: (row: ChildRow | OneOffRow | OrphanRow) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  highlightRowId?: string | null;
  /** Deliverable id whose row should flash to signal an auto-RAG change.
   *  Passed through to DeliverableTableBodyRows unchanged. */
  ragFlashRowId?: string | null;
  /** RAG rating from the mutation response — passed alongside ragFlashRowId
   *  because the list query may not have re-fetched by render time. */
  ragFlashRagRating?: RagRating | null;
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
  /** Sort wiring is intentionally NOT supported in the grouped view —
   *  each group has its own canonical sort. The header still renders
   *  for visual symmetry with sequential mode; clicks are inert. */
  sortColumn?: DeliverableSortColumn | null;
  sortDirection?: DeliverableSortDirection;
  onSortChange?: (next: {
    column: DeliverableSortColumn | null;
    direction: DeliverableSortDirection;
  }) => void;
  expandedItemId?: string | null;
  renderExpandedRow?: (deliverable: Deliverable) => React.ReactNode;
}

export default function DeliverableGroupedView({
  groups,
  // expandedScheduleIds / onToggleExpand are intentionally not consumed
  // here — the grouped view manages recurring-parent expand state
  // per-group (see groupExpanded below) so a distributed parent's
  // appearances toggle independently. The sequential view still uses
  // the global props from the parent.
  onRowClick,
  onAction,
  highlightRowId,
  ragFlashRowId,
  ragFlashRagRating,
  milestonesById,
  expandedItemId,
  renderExpandedRow,
}: DeliverableGroupedViewProps) {
  const [collapsed, setCollapsed] =
    useState<Record<DeliverableGroupKey, boolean>>(readAllCollapsed);

  const toggle = useCallback((key: DeliverableGroupKey) => {
    setCollapsed((prev) => {
      const next = !prev[key];
      writeCollapsedToStorage(key, next);
      return { ...prev, [key]: next };
    });
  }, []);

  // Per-group recurring-parent expand state. A distributed parent
  // appears in multiple buckets with the SAME deliverable id; keying
  // the expand set by the bare id would cascade — expanding the parent
  // in Awaiting customer action would also expand its (different)
  // appearance in Upcoming. Namespace the expand key by group so each
  // bucket's chevron toggles independently. The global
  // expandedScheduleIds / onToggleExpand from the parent stay unused
  // here (they still drive the sequential view, which has no
  // duplication).
  const [groupExpanded, setGroupExpanded] = useState<Set<string>>(
    new Set(),
  );
  const makeGroupExpandApi = useCallback(
    (groupKey: DeliverableGroupKey) => {
      const composite = (id: string) => `${groupKey}::${id}`;
      // Bare-id Set for this group so DeliverableTableBodyRows'
      // `expandedScheduleIds.has(row.deliverable.id)` check works
      // unchanged.
      const ids = new Set<string>();
      for (const k of groupExpanded) {
        const prefix = `${groupKey}::`;
        if (k.startsWith(prefix)) ids.add(k.slice(prefix.length));
      }
      const onToggle = (parentId: string) => {
        setGroupExpanded((prev) => {
          const next = new Set(prev);
          const key = composite(parentId);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
      };
      return { ids, onToggle };
    },
    [groupExpanded],
  );

  const visibleGroups = groups.filter((g) => g.rows.length > 0);
  const colCount = DELIVERABLE_TABLE_COLUMN_COUNT;

  // Sort visible groups by GROUP_ORDER even if upstream passes them in
  // another order — locks the layout invariant in one place.
  const orderedGroups: DeliverableRowGroup[] = GROUP_ORDER.map((key) =>
    visibleGroups.find((g) => g.key === key),
  ).filter((g): g is DeliverableRowGroup => g !== undefined);

  return (
    <div
      data-testid="deliverable-grouped-table"
      className="overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
    >
      <table
        data-testid="deliverable-table"
        className="min-w-full border-collapse text-body-sm"
      >
        <thead className="sticky top-0 z-[5] bg-slate-50 dark:bg-slate-800">
          <DeliverableTableHeadRow />
        </thead>
        {orderedGroups.map((group) => {
          const collapsible = COLLAPSIBLE_KEYS.has(group.key);
          // Non-collapsible groups are always rendered expanded
          // regardless of any stored value, locking the "live work
          // always visible" invariant in one place.
          const isCollapsed = collapsible && collapsed[group.key];
          const bodyId = `deliverable-group-body-${group.key}`;
          const Icon = isCollapsed ? ChevronRight : ChevronDown;

          const headingContent = (
            <span className="inline-flex items-center gap-2">
              {collapsible && (
                <Icon
                  className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400"
                  aria-hidden="true"
                />
              )}
              <span className="text-overline uppercase text-slate-700 dark:text-slate-200">
                {group.title}
              </span>
              <span
                data-testid={`deliverable-group-count-${group.key}`}
                className="rounded-full bg-slate-200 px-2 py-0.5 text-caption text-slate-600 dark:bg-slate-700 dark:text-slate-300"
              >
                {group.rows.length}
              </span>
              {group.subLabel && (
                <span className="text-caption text-slate-500 dark:text-slate-400">
                  · {group.subLabel}
                </span>
              )}
            </span>
          );

          return (
            <tbody
              key={group.key}
              id={bodyId}
              data-testid={`deliverable-group-${group.key}`}
              className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700"
            >
              <tr>
                <td
                  colSpan={colCount}
                  className="bg-slate-200/70 dark:bg-slate-900/70 px-2 py-1.5"
                >
                  {collapsible ? (
                    <button
                      type="button"
                      data-testid={`deliverable-group-header-${group.key}`}
                      aria-expanded={!isCollapsed}
                      aria-controls={bodyId}
                      onClick={() => toggle(group.key)}
                      className={clsx(
                        "flex w-full items-center justify-between rounded-md px-1 py-0 text-left",
                        "hover:bg-slate-100/60 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                        "dark:hover:bg-slate-700/50",
                      )}
                    >
                      {headingContent}
                      <span className="sr-only">
                        {isCollapsed ? "Expand" : "Collapse"} {group.title}
                      </span>
                    </button>
                  ) : (
                    <div
                      data-testid={`deliverable-group-header-${group.key}`}
                      className="flex w-full items-center justify-between px-1 py-0"
                    >
                      {headingContent}
                    </div>
                  )}
                </td>
              </tr>
              {!isCollapsed &&
                (() => {
                  const { ids, onToggle } = makeGroupExpandApi(group.key);
                  return (
                    <DeliverableTableBodyRows
                      rows={group.rows}
                      expandedScheduleIds={ids}
                      onToggleExpand={onToggle}
                      onRowClick={onRowClick}
                      onAction={onAction}
                      highlightRowId={highlightRowId}
                      ragFlashRowId={ragFlashRowId}
                      ragFlashRagRating={ragFlashRagRating}
                      milestonesById={milestonesById}
                      expandedItemId={expandedItemId}
                      renderExpandedRow={renderExpandedRow}
                    />
                  );
                })()}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
